import { useMemo, type ReactElement } from 'react';
import { StackedMetricsStatusChartSection } from './StackedMetricsStatusChartSection';
import type { MetricsStatusChartPanelDisplayModel } from './useMetricsStatusChartPanelModel';
import type { MetricsStatusChartPanelModelToolbarState } from './useMetricsStatusChartPanelModel';
import type { MiniMetricLineChartDatum } from '@/features/executions/utils/chartData';
import type { MetricValueItem } from '@/features/metrics/components/metricValueItems';
import type { MetricCardItem } from '@/features/metrics/components/statusMetricCards';
import type { MetricsScope } from '@/features/metrics/metricsHelpers';
import type { TableStatusOption } from '@/features/shared/components/tableSystem/tableToolbar.types';
import type { UiTone } from '@/theme/uiTokens';

type DetailMetricsStatusChartSectionProps<TStatus extends string> = {
  title: string;
  searchAriaLabel: string;
  noRowsBaseText: string;
  scope: MetricsScope;
  projectId: string | null;
  availableTags: string[];
  metricCards: MetricCardItem[];
  metricValueItems: MetricValueItem[];
  loading?: boolean;
  statusCardSkeletonCount?: number;
  toolbarState: MetricsStatusChartPanelModelToolbarState<TStatus>;
  statusOptions?: Array<TableStatusOption<TStatus>>;
  suppressAnimation: boolean;
  showPoints: boolean;
  stackedChartData: {
    data: Array<Record<string, string | number>>;
    series: Array<{ key: string; label: string; color?: string; colorKey?: UiTone }>;
  } | null;
  passRateChartData: Array<Record<string, string | number | null>> | null;
  frequencyChartData: Array<Record<string, string | number | null>> | null;
  durationChartData: Array<Record<string, string | number | null>> | null;
  flakeScoreChartData?: Array<Record<string, string | number | null>> | null;
};

const mapMetricValueTone = (
  tone: MetricValueItem['valueTone'],
): 'neutral' | 'green' | 'red' | 'orange' | 'brown' | 'blue' | 'purple' | 'teal' => {
  switch (tone) {
    case 'default':
    case 'neutral':
    case 'success':
    case 'green':
      return 'green';
    case 'error':
    case 'red':
      return 'red';
    case 'warning':
    case 'orange':
    case 'yellow':
      return 'orange';
    case 'brown':
      return 'brown';
    case 'info':
    case 'blue':
      return 'blue';
    case 'primary':
    case 'teal':
      return 'teal';
    case 'pink':
    case 'grey':
    case 'transparent':
      return 'neutral';
    case 'purple':
      return 'purple';
  }
};

const toMetricSummary = (
  metricValueItems: MetricValueItem[],
  id: MetricValueItem['id'],
): MetricsStatusChartPanelDisplayModel['passRateSummary'] => {
  const item = metricValueItems.find((metricItem) => metricItem.id === id);
  if (item === undefined) return null;

  return {
    valueLabel: item.valueLabel,
    valueTone: mapMetricValueTone(item.valueTone),
    deltaLabel: item.deltaLabel,
    deltaValue: item.deltaValue,
    deltaTone: item.deltaTone,
    isMissing: item.valueLabel === '-',
  };
};

const toMiniMetricChartData = (
  data: Array<Record<string, string | number | null>> | null,
  key: string,
): MiniMetricLineChartDatum[] | null =>
  data?.map((row) => {
    const value = row[key];
    const measuredValue = typeof value === 'number' && Number.isFinite(value) ? value : null;
    return {
      label: String(row.label),
      measuredValue,
      carryForwardValue: measuredValue ?? 0,
    };
  }) ?? null;

export const DetailMetricsStatusChartSection = <TStatus extends string>({
  title,
  searchAriaLabel,
  noRowsBaseText,
  scope,
  projectId,
  availableTags,
  metricCards,
  metricValueItems,
  loading = false,
  statusCardSkeletonCount,
  toolbarState,
  statusOptions = [],
  suppressAnimation,
  showPoints,
  stackedChartData,
  passRateChartData,
  frequencyChartData,
  durationChartData,
  flakeScoreChartData = null,
}: DetailMetricsStatusChartSectionProps<TStatus>): ReactElement => {
  const hasResolvedChartPayload =
    stackedChartData !== null ||
    passRateChartData !== null ||
    frequencyChartData !== null ||
    durationChartData !== null ||
    flakeScoreChartData !== null;
  const effectiveLoading = loading || !hasResolvedChartPayload;
  const modelOverride = useMemo<MetricsStatusChartPanelDisplayModel>(
    () => ({
      statusChartData: stackedChartData,
      passRateChartData,
      frequencyChartData,
      durationChartData,
      flakeScoreChartData,
      passRateMiniChartData: toMiniMetricChartData(passRateChartData, 'passRate'),
      frequencyMiniChartData: toMiniMetricChartData(frequencyChartData, 'frequency'),
      durationMiniChartData: toMiniMetricChartData(durationChartData, 'duration'),
      flakeScoreMiniChartData: toMiniMetricChartData(flakeScoreChartData, 'flakeScore'),
      showStatusChartSkeleton: false,
      showChartErrorState: false,
      passRateSummary: toMetricSummary(metricValueItems, 'passRate'),
      frequencySummary: toMetricSummary(metricValueItems, 'frequency'),
      durationSummary: toMetricSummary(metricValueItems, 'avgDuration'),
      flakeScoreSummary: toMetricSummary(metricValueItems, 'flakeScore'),
      facetCounts: {
        statusCounts: {},
        entityCounts: {},
      },
    }),
    [
      durationChartData,
      flakeScoreChartData,
      frequencyChartData,
      metricValueItems,
      passRateChartData,
      stackedChartData,
    ],
  );

  return (
    <StackedMetricsStatusChartSection<TStatus>
      title={title}
      searchAriaLabel={searchAriaLabel}
      noRowsBaseText={noRowsBaseText}
      scope={scope}
      projectId={projectId}
      availableTags={availableTags}
      statusOptions={statusOptions}
      metricCards={metricCards}
      loading={effectiveLoading}
      statusCardSkeletonCount={statusCardSkeletonCount}
      toolbarState={toolbarState}
      modelOverride={modelOverride}
      suppressAnimation={suppressAnimation}
      showPoints={showPoints}
      showSearch={false}
    />
  );
};
